/**
 * Docs site — vanilla TS island.
 *
 * NO React, NO Spectrum. Bundle objetivo < 30 KB gzip. Wires up:
 *   - Copy buttons (idéntico al landing client)
 *   - On-this-page scrollspy (IntersectionObserver)
 *   - Cmd+K search modal con carga lazy de Pagefind
 *   - Feedback inline (cosmético hasta endpoint Fase 13)
 *
 * El sidebar y el contenido son server-rendered; este script solo
 * agrega interactividad.
 */

import "./styles/docs.css";

function ready(fn: () => void): void {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fn, { once: true });
  } else {
    fn();
  }
}

/* ─────────── Copy buttons ─────────── */

function wireCopyButtons(): void {
  document
    .querySelectorAll<HTMLButtonElement>("[data-copy]")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        const source = btn.dataset.copySource ?? "";
        if (!navigator.clipboard) return;
        navigator.clipboard
          .writeText(source)
          .then(() => {
            btn.setAttribute("aria-pressed", "true");
            const original = btn.textContent ?? "Copiar";
            btn.textContent = "Copiado";
            setTimeout(() => {
              btn.setAttribute("aria-pressed", "false");
              btn.textContent = original;
            }, 1500);
          })
          .catch(() => {
            /* fail silently */
          });
      });
    });
}

/* ─────────── On-this-page scrollspy ─────────── */

function wireTocScrollspy(): void {
  const tocLinks = Array.from(
    document.querySelectorAll<HTMLAnchorElement>("[data-toc-link]"),
  );
  if (tocLinks.length === 0) return;

  const idToLink = new Map<string, HTMLAnchorElement>();
  for (const a of tocLinks) {
    const id = a.dataset.tocLink;
    if (id) idToLink.set(id, a);
  }

  function activate(id: string): void {
    for (const a of tocLinks) {
      a.classList.toggle("toc__link--active", a.dataset.tocLink === id);
    }
  }

  const headings: Element[] = [];
  for (const id of idToLink.keys()) {
    const el = document.getElementById(id);
    if (el) headings.push(el);
  }
  if (headings.length === 0) return;

  let activeId: string | null = null;

  const observer = new IntersectionObserver(
    (entries) => {
      // Find the topmost intersecting heading.
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length > 0) {
        const id = visible[0]!.target.id;
        if (id !== activeId) {
          activeId = id;
          activate(id);
        }
      }
    },
    {
      // Activate when heading enters the top 30% viewport band.
      rootMargin: "0px 0px -70% 0px",
      threshold: 0,
    },
  );

  for (const h of headings) observer.observe(h);

  // Initial activation by hash or first heading
  const initial =
    (location.hash && location.hash.slice(1)) ||
    headings[0]?.id ||
    null;
  if (initial) activate(initial);
}

/* ─────────── Search (Cmd+K, Pagefind) ─────────── */

interface PagefindResult {
  id: string;
  data: () => Promise<{
    url: string;
    excerpt: string;
    meta: { title?: string };
  }>;
}

interface PagefindAPI {
  search: (query: string) => Promise<{ results: PagefindResult[] }>;
}

let pagefindPromise: Promise<PagefindAPI | null> | null = null;

function loadPagefind(): Promise<PagefindAPI | null> {
  if (pagefindPromise) return pagefindPromise;
  const base = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");
  const url = `${base}/_pagefind/pagefind.js`;
  pagefindPromise = import(/* @vite-ignore */ url)
    .then((mod) => mod as PagefindAPI)
    .catch(() => null);
  return pagefindPromise;
}

function wireSearch(): void {
  const trigger = document.querySelector<HTMLButtonElement>(
    "[data-search-trigger]",
  );
  const modal = document.querySelector<HTMLElement>("[data-search-modal]");
  const input = document.querySelector<HTMLInputElement>(
    "[data-search-input]",
  );
  const resultsEl = document.querySelector<HTMLElement>(
    "[data-search-results]",
  );
  if (!trigger || !modal || !input || !resultsEl) return;

  const emptyEl = resultsEl.querySelector<HTMLElement>("[data-search-empty]");

  function open(): void {
    modal!.dataset.open = "true";
    modal!.removeAttribute("hidden");
    setTimeout(() => input!.focus(), 0);
  }

  function close(): void {
    modal!.dataset.open = "false";
    modal!.setAttribute("hidden", "");
  }

  trigger.addEventListener("click", open);
  modal.addEventListener("click", (e) => {
    if (e.target === modal) close();
  });

  document.addEventListener("keydown", (e) => {
    const isOpen = modal.dataset.open === "true";
    const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
    if (cmdK) {
      e.preventDefault();
      if (isOpen) close();
      else open();
    } else if (e.key === "Escape" && isOpen) {
      e.preventDefault();
      close();
    }
  });

  let lastQuery = "";
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  async function runSearch(query: string): Promise<void> {
    if (query === lastQuery) return;
    lastQuery = query;

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      renderEmpty("Escribe al menos 2 caracteres para buscar.");
      return;
    }

    const pagefind = await loadPagefind();
    if (!pagefind) {
      renderEmpty(
        "Búsqueda no disponible (Pagefind no encontró su índice). Ejecuta `pnpm pagefind` después del build.",
      );
      return;
    }

    if (lastQuery !== query) return; // stale

    try {
      const search = await pagefind.search(trimmed);
      if (lastQuery !== query) return; // stale

      if (search.results.length === 0) {
        renderEmpty(`Sin resultados para "${trimmed}".`);
        return;
      }

      const top = search.results.slice(0, 8);
      const details = await Promise.all(top.map((r) => r.data()));

      if (lastQuery !== query) return; // stale

      resultsEl!.innerHTML = "";
      details.forEach((d, i) => {
        const a = document.createElement("a");
        a.className = "search-modal__result";
        a.href = d.url;
        a.setAttribute("role", "option");
        if (i === 0) a.setAttribute("aria-selected", "true");
        const title = document.createElement("div");
        title.className = "search-modal__result-title";
        title.textContent = d.meta.title ?? d.url;
        const excerpt = document.createElement("div");
        excerpt.className = "search-modal__result-excerpt";
        excerpt.innerHTML = d.excerpt;
        a.appendChild(title);
        a.appendChild(excerpt);
        resultsEl!.appendChild(a);
      });
    } catch (err) {
      console.error("[search] failed:", err);
      renderEmpty("Error de búsqueda. Intenta de nuevo.");
    }
  }

  function renderEmpty(message: string): void {
    resultsEl!.innerHTML = "";
    const p = emptyEl?.cloneNode(false) as HTMLElement | undefined;
    if (p) {
      p.textContent = message;
      p.removeAttribute("hidden");
      resultsEl!.appendChild(p);
    } else {
      const fb = document.createElement("p");
      fb.className = "search-modal__empty";
      fb.textContent = message;
      resultsEl!.appendChild(fb);
    }
  }

  input.addEventListener("input", () => {
    const q = input.value;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      void runSearch(q);
    }, 120);
  });
}

/* ─────────── Feedback inline ─────────── */

function wireFeedback(): void {
  document.querySelectorAll<HTMLElement>("[data-feedback]").forEach((box) => {
    const thanks = box.querySelector<HTMLElement>("[data-feedback-thanks]");
    const buttons = Array.from(
      box.querySelectorAll<HTMLButtonElement>("[data-feedback-vote]"),
    );
    buttons.forEach((btn) => {
      btn.addEventListener("click", () => {
        buttons.forEach((b) =>
          b.setAttribute(
            "aria-pressed",
            b === btn ? "true" : "false",
          ),
        );
        if (thanks) thanks.removeAttribute("hidden");
        // El POST real al backend se conecta en Fase 13.
      });
    });
  });
}

ready(() => {
  wireCopyButtons();
  wireTocScrollspy();
  wireSearch();
  wireFeedback();
});
